/**
 * Plano de mídia do cliente.
 *
 * O SFU manda o Opus intocado; a degradação de RF é responsabilidade daqui
 * (docs/05-audio-dsp-wasm.md §1). Nesta versão a cadeia de RX ainda é mínima —
 * o DSP em Rust/WASM é a Fase 3. O que já existe é a estrutura: cada trilha que
 * chega passa por um nó de ganho controlado pelo SNR, então o sinal fraco
 * realmente soa fraco.
 */

import type { RXParams, Signal } from './protocol'

/**
 * Log de áudio visível na tela, não só no console.
 *
 * Em celular não dá para abrir o devtools — quem está debugando um "mudo"
 * em produção só tem o que aparece na própria tela. Buffer pequeno e em
 * memória de propósito: é para um print, não para telemetria.
 */
const MAX_DEBUG_LINES = 200
const debugLines: string[] = []

export function logAudio(...args: unknown[]): void {
  const line = args
    .map((a) => (typeof a === 'string' ? a : JSON.stringify(a)))
    .join(' ')
  const hora = new Date().toISOString().slice(11, 19)
  debugLines.push(`${hora}  ${line}`)
  if (debugLines.length > MAX_DEBUG_LINES) debugLines.shift()
  console.debug(line)
}

/** Cópia do log de áudio acumulado nesta sessão, para exibir na tela. */
export function getAudioDebugLog(): string[] {
  return debugLines
}

/**
 * Busca os servidores ICE do servidor.
 *
 * As credenciais de TURN expiram, então são pedidas a cada negociação em vez de
 * guardadas. Se a chamada falhar, cai num STUN público: pior que TURN, melhor
 * que nada — e a falha aparece no console em vez de virar um mudo inexplicável.
 */
export async function fetchIceServers(): Promise<RTCIceServer[]> {
  try {
    const r = await fetch('/api/ice')
    if (!r.ok) throw new Error(String(r.status))
    const d = (await r.json()) as { ice_servers?: RTCIceServer[]; has_turn?: boolean }
    if (!d.has_turn) {
      logAudio('[audio] sem TURN: em rede móvel com CGNAT o áudio pode não fechar')
    }
    return d.ice_servers ?? []
  } catch (e) {
    logAudio('[audio] não consegui os servidores ICE, usando STUN público', String(e))
    return [{ urls: 'stun:stun.l.google.com:19302' }]
  }
}

/** Configuração de captura: TUDO que o navegador faria de "melhoria" atrapalha. */
const CAPTURE: MediaStreamConstraints = {
  audio: {
    channelCount: 1,
    sampleRate: 48000,
    // Os processamentos automáticos do navegador removem exatamente o que
    // queremos controlar. Sem desligar, a simulação de RF fica sabotada.
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
  },
  video: false,
}

export interface AudioEngineOptions {
  /** Envia sinalização WebRTC pelo control plane. */
  sendSignal: (sig: Signal) => void
  onStateChange?: (state: RTCPeerConnectionState) => void
}

/** Um trecho de bipe, como o servidor o define (internal/roger). */
export interface RogerStep {
  freq_hz: number
  to_hz?: number
  ms: number
}

/** Um bipe do catálogo. */
export interface RogerTone {
  id: string
  name: string
  note: string
  etiqueta?: string
  steps?: RogerStep[]
}

/**
 * Nível do bipe no áudio transmitido.
 *
 * Abaixo da voz de propósito: um roger mais alto que a modulação é o defeito
 * clássico dos módulos baratos de PX, e quem recebe abaixa o volume no meio da
 * frase seguinte.
 */
const TX_TONE_LEVEL = 0.28

/** Nível do retorno local — o "monitor" do rádio. Só para o operador saber que o bipe saiu. */
const MONITOR_LEVEL = 0.1

/** Rampa anti-clique. Um tom que liga em degrau estala, e o estalo vai no ar junto. */
const RAMP_S = 0.005

/**
 * Ruído de banda — Web Audio API pura, sem WASM (docs/05 §3.3 é a versão
 * completa, com QRN atmosférico e ruído físico; isto é a fatia honesta que dá
 * para fazer com osciladores e filtros comuns).
 *
 * Nunca existe silêncio absoluto: mesmo com o canal livre, um chiado baixo e
 * constante fica de fundo — "silêncio total soa errado" (doc 05 §3.3). Sob um
 * sinal, o ruído sobe conforme o SNR cai: forte quase some, fraco luta contra
 * o chiado, exatamente como um receptor de verdade.
 */
const IDLE_NOISE_LEVEL = 0.02
const SIGNAL_NOISE_MAX = 0.1

/**
 * Ganho extra aplicado só à VOZ recebida, não ao ruído.
 *
 * CAPTURE desliga autoGainControl de propósito (linha acima) — sem AGC, a
 * voz crua de um microfone comum sai baixa, e ganho 1.0 (sem boost nenhum)
 * deixava a voz mais fraca que o chiado de banda em SNR moderado, mesmo o
 * chiado sendo objetivamente mais baixo em número. Isto é o estágio de
 * ganho de AF que um rádio de verdade tem no receptor — não é trapacear a
 * simulação, é a etapa que faltava depois de tirar o AGC do navegador.
 */
const VOICE_GAIN_BOOST = 2.2

/**
 * Piso de ganho para sinal sem squelch fechando: audível, claramente fraco,
 * nunca mudo. Distância grande demais ou equipamento mal ajustado custam
 * volume e qualidade — nunca a conversa inteira (ver aplicarGate).
 */
const MIN_SIGNAL_GAIN = 0.035

/** Largura de filtro padrão (SSB normal) — docs/05 §3.2. */
const DEFAULT_FILTER_HZ = 2400

/** Limiar padrão do squelch (SNR em dB) — ligado por padrão, como um rádio de verdade. */
const DEFAULT_SQUELCH_DB = 0

/** Nível dos tons de "atracou na repetidora" — local, nunca transmitido. */
const LOCK_TONE_LEVEL = 0.22

export class AudioEngine {
  private pc: RTCPeerConnection | null = null
  private mic: MediaStream | null = null
  private ctx: AudioContext | null = null
  /** Sonda periódica do par de candidato ativo — ver start(). */
  private statsTimer: ReturnType<typeof setInterval> | null = null

  /**
   * Cadeia de transmissão.
   *
   * A trilha que vai para o WebRTC deixou de ser o microfone cru e passou a ser
   * a saída de um grafo — é isso que permite misturar o bipe AO SINAL, em vez
   * de tocá-lo no receptor. A diferença não é de implementação: bipe misturado
   * aqui percorre o mesmo caminho de RF da voz, então estação fraca tem roger
   * fraco e num caminho marginal o bipe some junto com a palavra. Tocado no
   * receptor, seria enfeite de interface fingindo ser sinal.
   *
   *   mic ──▶ micGain ──┐
   *                     ├──▶ txDest ──▶ RTCPeerConnection
   *   bipe ─────────────┘
   */
  private txDest: MediaStreamAudioDestinationNode | null = null
  private micGain: GainNode | null = null
  private txTrack: MediaStreamTrack | null = null

  /** Oscilador do bipe em curso, para cancelar se outro começar por cima. */
  private toneOsc: OscillatorNode | null = null

  /**
   * Chave manual de CW: osciloador ligado por `startManualKey`, sem duração
   * definida de antemão — ao contrário de `toneOsc`/`playTone`, que já
   * recebe a sequência inteira de passos. `keyGain` fica separado do ganho
   * genérico de `playTone` porque a rampa de descida de `stopManualKey`
   * precisa partir do valor ATUAL do ganho, não de uma automação agendada
   * pra um tempo futuro que nunca chegaria (ver stopManualKey).
   */
  private keyOsc: OscillatorNode | null = null
  private keyGain: GainNode | null = null

  /** Um nó de ganho por transmissor, para aplicar o SNR de cada um. */
  private rxGains = new Map<string, GainNode>()
  private rxOrder: string[] = []

  /**
   * Filtro de FI por transmissor — molda o TIMBRE conforme a largura
   * escolhida pelo OUVINTE (filterHz). É o mesmo controle para todo mundo que
   * se ouve, então um nó por transmissor ativo, todos ajustados juntos por
   * setFilterHz.
   */
  private rxFilters = new Map<string, BiquadFilterNode>()
  private filterHz = DEFAULT_FILTER_HZ

  /**
   * Ruído de banda: uma cadeia só, compartilhada entre todos os
   * transmissores — o piso de ruído é propriedade do RECEPTOR, não soma um
   * por transmissor. noiseLevels guarda quanto cada transmissor ativo PEDE de
   * ruído (conforme o SNR dele); o nó de ganho segue o pedido mais alto.
   */
  private noiseSrc: AudioBufferSourceNode | null = null
  private noiseGain: GainNode | null = null
  private noiseLevels = new Map<string, number>()
  /** "Áudio limpo": zera o chiado sem mexer no ganho da voz — ver setNoiseEnabled. */
  private noiseEnabled = true

  /**
   * Squelch: null = desligado (sempre deixa passar, comportamento de antes).
   * Um número é o limiar de SNR em dB — abaixo disso, silêncio (voz E o
   * chiado de fundo, que é o ponto inteiro do squelch: parar de ouvir ruído
   * quando não há ninguém no ar). lastSnr guarda o SNR mais recente de cada
   * transmissor para reavaliar o corte na hora que o limiar muda, sem
   * esperar o próximo RXParams.
   */
  private squelchDb: number | null = DEFAULT_SQUELCH_DB
  private lastSnr = new Map<string, number>()
  /** Se ALGUM sinal está passando pelo squelch agora — só para detectar a
   *  transição e tocar o clique de abrir/fechar uma vez, não a cada quadro. */
  private squelchAberto = false

  private opts: AudioEngineOptions

  constructor(opts: AudioEngineOptions) {
    this.opts = opts
  }

  /**
   * Cria e destrava o AudioContext — a ÚNICA parte que precisa rodar dentro
   * do gesto síncrono do usuário (clique/toque), antes de qualquer `await`;
   * no iOS isso é regra dura. `start()` chama isto sozinho se ainda não
   * tiver sido chamado, mas quem quer ligar áudio SEM clique nenhum (entrar
   * já deixa o rádio pronto) precisa chamar `unlock()` direto de dentro do
   * clique de entrar/cadastrar — o resto (microfone, WebRTC) pode vir depois,
   * de um efeito, sem gesto nenhum.
   *
   * Idempotente: chamar de novo com o contexto já criado não faz nada.
   */
  unlock(): void {
    if (this.ctx) return
    this.ctx = new AudioContext({ sampleRate: 48000 })
    // Sem esperar: resume() já teria que ter sido pedido dentro do gesto —
    // esperar aqui tiraria a criação do contexto do mesmo tick síncrono do
    // clique, que é o requisito de verdade.
    void this.ctx.resume()
    this.startNoise(this.ctx)
  }

  /** Prepara a sessão de mídia — microfone e conexão WebRTC. */
  async start(): Promise<void> {
    if (this.pc) return
    this.unlock()
    const ctx = this.ctx
    if (!ctx) return
    await ctx.resume()

    const pc = new RTCPeerConnection({
      // Vindos do servidor, não fixos aqui: TURN só funciona se os DOIS lados
      // o conhecerem, e com STUN no código configurar TURN no servidor não
      // adiantaria nada. O sintoma seria "não funciona em 4G" com o TURN no ar
      // e aparentemente certo.
      iceServers: await fetchIceServers(),
    })
    this.pc = pc

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        logAudio('[audio] -> candidate', e.candidate.type, e.candidate.protocol)
        this.opts.sendSignal({ kind: 'candidate', candidate: e.candidate.toJSON() })
      } else {
        logAudio('[audio] fim dos candidatos')
      }
    }

    pc.onconnectionstatechange = () => {
      logAudio('[audio] estado:', pc.connectionState, 'ice=', pc.iceConnectionState)
      this.opts.onStateChange?.(pc.connectionState)
    }
    pc.onicegatheringstatechange = () => {
      logAudio('[audio] ice gathering:', pc.iceGatheringState)
    }

    pc.ontrack = (e) => {
      logAudio('[audio] <- trilha recebida', e.track.kind, e.streams[0]?.id ?? '(sem stream)')
      this.attachTrack(e)
    }

    // Captura o microfone já, mas silenciado. Pedir permissão no primeiro PTT
    // adicionaria um diálogo bem no momento em que a pessoa quer falar.
    this.mic = await navigator.mediaDevices.getUserMedia(CAPTURE)
    logAudio('[audio] microfone capturado')
    const micTrack = this.mic.getAudioTracks()[0]
    if (micTrack) {
      const src = ctx.createMediaStreamSource(this.mic)
      const micGain = ctx.createGain()
      micGain.gain.value = 0 // half-duplex: o PTT é que abre
      const dest = ctx.createMediaStreamDestination()
      src.connect(micGain)
      micGain.connect(dest)

      this.micGain = micGain
      this.txDest = dest

      const out = dest.stream.getAudioTracks()[0]
      if (out) {
        // enabled continua sendo a chave-geral da portadora: sem ela, o
        // navegador subiria pacotes o tempo todo, e "não estou transmitindo"
        // deixaria de ser verdade na rede.
        out.enabled = false
        this.txTrack = out
        pc.addTrack(out, dest.stream)
      }
    }

    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)
    logAudio('[audio] -> offer')
    this.opts.sendSignal({ kind: 'offer', sdp: offer.sdp ?? '' })

    // Sonda o par de candidato ativo a cada 5 s — é o que mostra se a
    // conexão está indo por caminho direto (host/srflx) ou por relay (TURN),
    // e se os bytes seguem andando antes de uma queda. Sem isto, uma queda
    // de ICE só aparecia como "estado: failed", sem dizer qual caminho
    // morreu nem se já tinha parado de fluir antes disso.
    let ultimoBytes = -1
    this.statsTimer = setInterval(() => {
      void pc.getStats().then((report) => {
        const pares = [...report.values()].filter(
          (s) => s.type === 'candidate-pair' && s.state === 'succeeded',
        )
        const cands = Object.fromEntries(
          [...report.values()].filter((s) => s.type.includes('candidate')).map((s) => [s.id, s]),
        )
        for (const p of pares) {
          const local = cands[p.localCandidateId]
          const remote = cands[p.remoteCandidateId]
          const par = `${local?.candidateType ?? '?'} ↔ ${remote?.candidateType ?? '?'}`
          const bytes = p.bytesReceived ?? 0
          const parado = bytes === ultimoBytes ? ' (PARADO desde a última sonda)' : ''
          ultimoBytes = bytes
          logAudio('[audio] par ativo:', par, '· bytes recebidos:', bytes + parado)
        }
      })
    }, 5000)
  }

  /**
   * Processa sinalização vinda do servidor.
   *
   * Quem chama isto (ControlPlane) faz `void this.audio?.handleSignal(...)` —
   * fogo e esquece, porque a mensagem já chegou, não tem para quem devolver
   * erro. Sem o try/catch aqui, uma renegociação que falhasse (setRemoteDescription
   * rejeitando por qualquer motivo) virava uma promise rejeitada sem dono: some
   * no console, o áudio simplesmente nunca chega, e NADA no log explica por quê.
   */
  async handleSignal(sig: Signal): Promise<void> {
    const pc = this.pc
    if (!pc) {
      logAudio('[audio] sinal recebido sem peer connection:', sig.kind)
      return
    }
    logAudio('[audio] <-', sig.kind, 'signaling=', pc.signalingState)

    try {
      switch (sig.kind) {
        case 'offer': {
          // Renegociação: o servidor adicionou uma trilha para nós.
          await pc.setRemoteDescription({ type: 'offer', ...(sig.sdp ? { sdp: sig.sdp } : {}) })
          const answer = await pc.createAnswer()
          await pc.setLocalDescription(answer)
          this.opts.sendSignal({ kind: 'answer', sdp: answer.sdp ?? '' })
          logAudio('[audio] -> answer (renegociação respondida)')
          break
        }
        case 'answer':
          await pc.setRemoteDescription({ type: 'answer', ...(sig.sdp ? { sdp: sig.sdp } : {}) })
          break
        case 'candidate':
          if (sig.candidate) await pc.addIceCandidate(sig.candidate)
          break
      }
    } catch (e) {
      logAudio('[audio] ERRO processando sinal', sig.kind, String(e))
    }
  }

  /**
   * Liga ou desliga a transmissão inteira — portadora e microfone.
   *
   * Usa track.enabled em vez de recriar a trilha: a renegociação levaria
   * centenas de milissegundos e o operador falaria antes do canal abrir.
   */
  setTransmitting(on: boolean): void {
    if (this.txTrack) {
      this.txTrack.enabled = on
    } else if (on) {
      // Silencioso até aqui, mas não deveria: sem txTrack não existe
      // portadora nenhuma pra ligar — o botão de PTT mostra "no ar" porque
      // isso vem do servidor (tx_granted), sem depender disto batendo.
      logAudio('[audio] PTT pedido mas não há txTrack — nada será transmitido')
    }
    this.setMic(on)
  }

  /**
   * Abre ou fecha só o microfone, com a portadora no ar.
   *
   * É o estado em que o bipe acontece: quem solta o PTT para de ser ouvido na
   * hora, mas o transmissor continua no ar até o tom terminar — que é
   * exatamente o que um rádio com roger faz.
   */
  setMic(on: boolean): void {
    if (!this.micGain || !this.ctx) {
      if (on) {
        logAudio('[audio] PTT pedido mas micGain/ctx ausente — microfone não abre',
          'micGain=', !!this.micGain, 'ctx=', !!this.ctx)
      }
      return
    }
    // Rampa curta em vez de degrau: cortar a forma de onda no meio produz um
    // estalo, e ele iria no ar.
    this.micGain.gain.setTargetAtTime(on ? 1 : 0, this.ctx.currentTime, 0.005)
    if (on) {
      logAudio('[audio] microfone aberto, ctx.state=', this.ctx.state,
        'track.enabled=', this.mic?.getAudioTracks()[0]?.enabled,
        'track.muted=', this.mic?.getAudioTracks()[0]?.muted)
    }
  }

  /**
   * Toca um bipe e resolve quando ele acaba.
   *
   * Com transmit, o tom entra na cadeia de TX e vai no ar; o operador ouve uma
   * cópia baixa, que é o "monitor" do rádio. Sem transmit, é só a prévia da
   * tela de configuração e nada sai daqui.
   */
  playTone(tone: RogerTone | null, transmit: boolean): Promise<void> {
    const ctx = this.ctx
    const steps = tone?.steps ?? []
    if (!ctx || steps.length === 0) return Promise.resolve()

    // Bipe novo cancela o anterior. Sem isto, um PTT rápido faria o tom de
    // início ainda estar tocando quando o roger começasse, e os dois somariam.
    this.cancelTone()

    const osc = ctx.createOscillator()
    osc.type = 'sine'
    const gain = ctx.createGain()
    gain.gain.value = 0
    osc.connect(gain)

    const level = transmit ? TX_TONE_LEVEL : MONITOR_LEVEL * 3
    if (transmit && this.txDest) {
      gain.connect(this.txDest)
      const mon = ctx.createGain()
      mon.gain.value = MONITOR_LEVEL / TX_TONE_LEVEL
      gain.connect(mon)
      mon.connect(ctx.destination)
    } else {
      gain.connect(ctx.destination)
    }

    let t = ctx.currentTime + 0.02
    const start = t
    for (const s of steps) {
      const dur = s.ms / 1000
      if (s.freq_hz > 0) {
        osc.frequency.setValueAtTime(s.freq_hz, t)
        if (s.to_hz && s.to_hz > 0) {
          osc.frequency.linearRampToValueAtTime(s.to_hz, t + dur)
        }
        const r = Math.min(RAMP_S, dur / 3)
        gain.gain.setValueAtTime(0, t)
        gain.gain.linearRampToValueAtTime(level, t + r)
        gain.gain.setValueAtTime(level, t + dur - r)
        gain.gain.linearRampToValueAtTime(0, t + dur)
      }
      t += dur
    }

    osc.start(start)
    osc.stop(t + 0.02)
    this.toneOsc = osc

    return new Promise((resolve) => {
      osc.onended = () => {
        gain.disconnect()
        if (this.toneOsc === osc) this.toneOsc = null
        resolve()
      }
    })
  }

  /** Interrompe o bipe em curso. */
  cancelTone(): void {
    if (!this.toneOsc) return
    try {
      this.toneOsc.stop()
    } catch {
      // Já parado: nada a fazer.
    }
    this.toneOsc = null
  }

  /**
   * Liga o tom da chave manual de CW AGORA, sem duração definida — fica no
   * ar até `stopManualKey`. Mesma fiação de `playTone` no ramo
   * `transmit=true` (txDest + monitor local em nível reduzido), mas sem
   * lista de passos: quem decide quando o tom acaba é a MÃO do operador,
   * não uma sequência pré-calculada.
   */
  startManualKey(freqHz: number): void {
    const ctx = this.ctx
    if (!ctx || this.keyOsc) return

    const osc = ctx.createOscillator()
    osc.type = 'sine'
    osc.frequency.value = freqHz
    const gain = ctx.createGain()
    gain.gain.value = 0
    osc.connect(gain)

    if (this.txDest) {
      gain.connect(this.txDest)
      const mon = ctx.createGain()
      mon.gain.value = MONITOR_LEVEL / TX_TONE_LEVEL
      gain.connect(mon)
      mon.connect(ctx.destination)
    } else {
      gain.connect(ctx.destination)
    }

    const t = ctx.currentTime + 0.005
    gain.gain.setValueAtTime(0, t)
    gain.gain.linearRampToValueAtTime(TX_TONE_LEVEL, t + RAMP_S)
    osc.start(t)

    this.keyOsc = osc
    this.keyGain = gain
  }

  /**
   * Solta a chave manual: rampa de descida CURTA a partir do ganho ATUAL,
   * não uma automação agendada pra um tempo futuro — é por isso que não dá
   * pra reaproveitar `cancelTone` aqui: ele corta com `osc.stop()` na hora,
   * sem rampa nenhuma, e cada letra estalaria ao soltar.
   */
  stopManualKey(): void {
    const ctx = this.ctx
    const osc = this.keyOsc
    const gain = this.keyGain
    if (!ctx || !osc || !gain) return

    const t = ctx.currentTime
    gain.gain.cancelScheduledValues(t)
    gain.gain.setValueAtTime(gain.gain.value, t)
    gain.gain.linearRampToValueAtTime(0, t + RAMP_S)
    osc.stop(t + RAMP_S + 0.01)
    osc.onended = () => gain.disconnect()

    this.keyOsc = null
    this.keyGain = null
  }

  /**
   * Aplica os parâmetros de RF ao áudio de um transmissor.
   *
   * Ganho derivado do SNR, e a partir dele o quanto de ruído mistura sob a
   * voz — o resto do doc 05 §3 (QSB, QRN físico, dessintonia real) segue
   * exigindo o DSP em WASM da Fase 3.
   */
  applyRXParams(p: RXParams): void {
    this.logRXParamsChegou(p.tx_station_id, p.snr_db)
    this.lastSnr.set(p.tx_station_id, p.snr_db)
    this.aplicarGate(p.tx_station_id)
  }

  /** Quando cada transmissor mandou RXParams pela última vez ao log — throttle
   * de propósito, RXParams chega a 10 Hz e sem isto o buffer de log enche
   * em segundos sem sobrar espaço pra mais nada. */
  private lastRXParamsLog = new Map<string, number>()

  private logRXParamsChegou(stationID: string, snrDb: number): void {
    const agora = performance.now()
    const ultimo = this.lastRXParamsLog.get(stationID) ?? -Infinity
    if (agora - ultimo < 3000) return
    this.lastRXParamsLog.set(stationID, agora)
    logAudio('[audio] RXParams de', stationID, 'snr=', snrDb.toFixed(1),
      '· trilha conhecida?', this.rxGains.has(stationID))
  }

  /**
   * Decide se ESTE transmissor passa pelo squelch, a partir do SNR mais
   * recente conhecido dele. Separado de applyRXParams porque setSquelch
   * também precisa reavaliar todo mundo na hora que o limiar muda — sem
   * esperar o próximo pacote de RXParams, que só chega com sinal ativo.
   */
  private aplicarGate(stationID: string): void {
    const gain = this.rxGains.get(stationID)
    if (!gain || !this.ctx) {
      // RXParams chegou (o S-meter se move), mas não existe nó de ganho para
      // este transmissor — a trilha de áudio dele nunca chegou por ontrack,
      // ou chegou com um stationID que não bate com este. Sem este log, isto
      // falha em silêncio pra sempre: nenhum outro caminho avisa.
      logAudio('[audio] RXParams sem trilha correspondente para', stationID,
        '— gains conhecidos:', [...this.rxGains.keys()].join(','))
      return
    }

    const snrDb = this.lastSnr.get(stationID) ?? -99
    const fechado = this.squelchDb !== null && snrDb < this.squelchDb

    // Mapeia SNR para volume: abaixo do limiar de SSB o sinal fica bem fraco,
    // acima de +20 dB não fica mais alto — é o AGC fazendo o trabalho dele.
    // VOICE_GAIN_BOOST entra só aqui, na voz — o cálculo de ruído logo abaixo
    // usa o norm CRU, sem boost, porque é ele quem decide o quanto de chiado
    // pedir, não o volume final que sai no alto-falante.
    //
    // MIN_SIGNAL_GAIN em vez de zero: má configuração de estação ou distância
    // grande demais custam volume e qualidade, nunca a conversa inteira — o
    // servidor já manda RXParams mesmo em caminho fisicamente fechado (ver
    // hub.go/PushRXParams), então aqui é onde a degradação vira realmente
    // audível em vez de sumir. Squelch continua podendo zerar de vez — é
    // controle visível e escolhido pelo operador, diferente disto.
    const norm = fechado ? 0 : Math.max(MIN_SIGNAL_GAIN, Math.min(1, (snrDb + 3) / 23))
    gain.gain.setTargetAtTime(norm * VOICE_GAIN_BOOST, this.ctx.currentTime, 0.05)

    // Quanto mais fraco o sinal, mais chiado sob a voz — nunca some de vez
    // com o squelch desligado, porque nem no sinal mais forte um receptor
    // real fica em silêncio total. Com o squelch fechado, é zero mesmo: é o
    // ponto inteiro do squelch, parar de ouvir ruído com o canal livre.
    this.noiseLevels.set(
      stationID,
      fechado ? 0 : IDLE_NOISE_LEVEL + (1 - norm) * (SIGNAL_NOISE_MAX - IDLE_NOISE_LEVEL),
    )
    this.updateNoiseMix()
  }

  /**
   * Muda a largura do filtro de FI do OUVINTE — molda o timbre de quem já
   * está sendo recebido, e vale para o próximo transmissor que entrar.
   */
  setFilterHz(hz: number): void {
    this.filterHz = hz
    if (!this.ctx) return
    const cutoff = 300 + hz
    for (const lp of this.rxFilters.values()) {
      lp.frequency.setTargetAtTime(cutoff, this.ctx.currentTime, 0.05)
    }
  }

  /**
   * Muda o limiar do squelch (SNR em dB) — ou desliga com null. Reavalia
   * todo mundo que já está tocando na hora, em vez de esperar o próximo
   * RXParams: mudar o controle tem que ter efeito imediato.
   */
  setSquelch(db: number | null): void {
    this.squelchDb = db
    for (const id of this.rxGains.keys()) this.aplicarGate(id)
    this.updateNoiseMix()
  }

  /**
   * Liga ou desliga o chiado de banda — "áudio limpo": só a voz, sem
   * simulação de ruído nenhuma. NÃO mexe no ganho da voz (aplicarGate segue
   * atenuando sinal fraco do jeito de sempre) — desligado, some só o chiado,
   * não a diferença entre sinal forte e fraco.
   */
  setNoiseEnabled(on: boolean): void {
    this.noiseEnabled = on
    this.updateNoiseMix()
  }

  /**
   * Toca o tom de "atracou na repetidora" — LOCAL, nunca transmitido. É
   * confirmação para quem ouve, ao contrário do bipe de PTT (docs/05 §5, "O
   * bipe é transmitido, não tocado no receptor"), que tem que sair no ar
   * porque é sinal de verdade entre duas estações. Este não é sinal nenhum:
   * é o próprio aparelho avisando o operador.
   */
  playLockTone(digital: boolean): void {
    if (!this.ctx) return
    if (digital) {
      // Chirp de dois tons rápidos — a assinatura sonora de "é digital", sem
      // fingir decodificar DMR/C4FM de verdade.
      this.beep(880, 0.06, 0)
      this.beep(1400, 0.06, 0.07)
    } else {
      // Courtesy tone clássico: um bipe único e morno.
      this.beep(900, 0.12, 0)
    }
  }

  /** Um bipe curto tocado direto no alto-falante, sem passar pelo TX. */
  private beep(freq: number, dur: number, delay: number): void {
    const ctx = this.ctx
    if (!ctx) return
    const osc = ctx.createOscillator()
    osc.type = 'sine'
    osc.frequency.value = freq
    const g = ctx.createGain()
    g.gain.value = 0
    osc.connect(g)
    g.connect(ctx.destination)

    const t = ctx.currentTime + 0.02 + delay
    const r = Math.min(0.008, dur / 3)
    g.gain.setValueAtTime(0, t)
    g.gain.linearRampToValueAtTime(LOCK_TONE_LEVEL, t + r)
    g.gain.setValueAtTime(LOCK_TONE_LEVEL, t + dur - r)
    g.gain.linearRampToValueAtTime(0, t + dur)
    osc.start(t)
    osc.stop(t + dur + 0.02)
    osc.onended = () => g.disconnect()
  }

  /** Gera o buffer de ruído branco e liga a cadeia de chiado de banda. */
  private startNoise(ctx: AudioContext): void {
    const buf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1

    const src = ctx.createBufferSource()
    src.buffer = buf
    src.loop = true

    // Mesmo timbre passa-faixa da voz: chiado de rádio, não estática de TV.
    const hp = ctx.createBiquadFilter()
    hp.type = 'highpass'
    hp.frequency.value = 300
    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = 3000

    const gain = ctx.createGain()
    gain.gain.value = IDLE_NOISE_LEVEL

    src.connect(hp)
    hp.connect(lp)
    lp.connect(gain)
    gain.connect(ctx.destination)
    src.start()

    this.noiseSrc = src
    this.noiseGain = gain
  }

  /**
   * O ganho de ruído segue o pedido mais alto entre os transmissores ativos.
   * Com o squelch desligado, nunca cai abaixo do chiado de banda vazia; com
   * o squelch ligado e ninguém passando, o piso é zero — silêncio de
   * verdade, que é o que o controle existe para dar.
   *
   * Na TRANSIÇÃO de fechado→aberto ou aberto→fechado, troca a rampa suave
   * por um pulso curto — o "psh" ao abrir e a cauda de ruído ao fechar do
   * doc 05 §3.6, tocados no mesmo nó em vez de um oscilador à parte, porque
   * é o próprio chiado que dá esse som num rádio de verdade.
   */
  private updateNoiseMix(): void {
    if (!this.noiseGain || !this.ctx) return

    let level = 0
    for (const v of this.noiseLevels.values()) level = Math.max(level, v)
    const algumAberto = level > 0
    const piso = this.squelchDb === null ? IDLE_NOISE_LEVEL : 0
    // "Áudio limpo" zera o alvo E as transições abriu/fechou: sem isto, o
    // pulso de "psh" do squelch (linhas abaixo) ainda tocaria mesmo com o
    // chiado desligado — ele é feito do próprio nó de ruído, não um efeito à
    // parte.
    const alvo = this.noiseEnabled ? Math.max(level, piso) : 0

    const abriu = this.noiseEnabled && this.squelchDb !== null && !this.squelchAberto && algumAberto
    const fechou = this.noiseEnabled && this.squelchDb !== null && this.squelchAberto && !algumAberto
    this.squelchAberto = algumAberto

    const g = this.noiseGain.gain
    const t = this.ctx.currentTime
    if (abriu || fechou) {
      const pico = fechou ? 0.16 : 0.1
      const dur = fechou ? 0.14 : 0.06
      // cancelAndHoldAtTime, não cancelScheduledValues: um setTargetAtTime já
      // em andamento não é uma automação AGENDADA para o futuro, é uma que já
      // começou — cancelScheduledValues só remove eventos com início >= t, e
      // deixaria a rampa anterior convergindo por baixo do pulso novo.
      // cancelAndHoldAtTime interrompe de verdade e trava no valor atual.
      g.cancelAndHoldAtTime(t)
      g.linearRampToValueAtTime(pico, t + 0.015)
      g.linearRampToValueAtTime(alvo, t + dur)
    } else {
      g.setTargetAtTime(alvo, t, 0.15)
    }
  }

  /**
   * Silencia um transmissor que saiu do ar.
   *
   * NÃO desmonta o grafo. O servidor mantém a trilha viva entre apertos de PTT
   * — recriá-la a cada vez emudecia o áudio a partir do segundo aperto —, e
   * como não chega trilha nova, não haveria `ontrack` para reconstruir o que
   * fosse destruído aqui. Os pacotes chegariam e ninguém ouviria.
   *
   * Um nó de ganho por estação que já transmitiu é custo desprezível; o
   * desmonte de verdade acontece em stop().
   */
  dropStation(stationID: string): void {
    const g = this.rxGains.get(stationID)
    if (g && this.ctx) {
      g.gain.setTargetAtTime(0, this.ctx.currentTime, 0.02)
    }
    this.noiseLevels.delete(stationID)
    // Também tira do lastSnr: o nó de ganho fica vivo (comentário acima), mas
    // sem isto uma troca de squelch mais tarde reavaliaria esta estação com o
    // SNR de quando ainda transmitia, e um sinal que já saiu do ar reabriria
    // sozinho.
    this.lastSnr.delete(stationID)
    this.updateNoiseMix()
  }

  stop(): void {
    if (this.statsTimer) clearInterval(this.statsTimer)
    this.statsTimer = null
    this.cancelTone()
    this.keyOsc = null
    this.keyGain = null
    this.mic?.getTracks().forEach((t) => t.stop())
    try {
      this.noiseSrc?.stop()
    } catch {
      // já parado: nada a fazer
    }
    this.pc?.close()
    void this.ctx?.close()
    this.pc = null
    this.mic = null
    this.ctx = null
    this.txDest = null
    this.micGain = null
    this.txTrack = null
    this.rxGains.clear()
    this.rxOrder = []
    this.rxFilters.clear()
    this.noiseSrc = null
    this.noiseGain = null
    this.noiseLevels.clear()
    this.lastSnr.clear()
    this.squelchAberto = false
  }

  /**
   * Liga a trilha recebida ao grafo de áudio.
   *
   * O servidor nomeia o stream como "station-<id>", e é assim que sabemos de
   * quem é o áudio — sem isso não dá para aplicar o SNR certo a cada um.
   */
  private attachTrack(e: RTCTrackEvent): void {
    if (!this.ctx) {
      logAudio('[audio] trilha chegou sem AudioContext — descartada')
      return
    }

    const stream = e.streams[0]
    const label = stream?.id ?? ''
    const stationID = label.startsWith('station-') ? label.slice('station-'.length) : label

    const src = this.ctx.createMediaStreamSource(stream ?? new MediaStream([e.track]))
    const gain = this.ctx.createGain()
    gain.gain.value = 0 // entra mudo; o primeiro RXParams define o nível

    // Filtro de FI do ouvinte: passa-alta fixo (mesmo corte grave do TX) e
    // passa-baixa na largura escolhida — setFilterHz move este nó depois.
    const hp = this.ctx.createBiquadFilter()
    hp.type = 'highpass'
    hp.frequency.value = 300
    const lp = this.ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = 300 + this.filterHz

    // Trilha repetida do mesmo transmissor: solta a anterior antes de trocar,
    // senão o grafo antigo continua ligado à saída e os dois somam.
    const anterior = this.rxGains.get(stationID)
    if (anterior) {
      anterior.disconnect()
      this.rxFilters.get(stationID)?.disconnect()
    } else {
      this.rxOrder.push(stationID)
    }

    src.connect(hp)
    hp.connect(lp)
    lp.connect(gain)
    gain.connect(this.ctx.destination)
    this.rxGains.set(stationID, gain)
    this.rxFilters.set(stationID, lp)

    // Safari precisa de um <audio> vivo para não suspender a trilha, mesmo
    // quando o som sai pelo AudioContext.
    const sink = new Audio()
    sink.srcObject = stream ?? null
    sink.muted = true
    void sink.play().catch((err) => logAudio('[audio] sink.play() falhou:', err?.name ?? String(err)))

    logAudio('[audio] trilha ligada ao grafo, stationID=', stationID, 'ctx.state=', this.ctx.state)
  }
}
